import { useState } from 'react'
import { motion } from 'framer-motion'
import { GlassmorphicCard } from '@/components/ui/glassmorphic-card'
import {
    FileText, Eye, Search, ChevronDown, FolderOpen,
    BookOpen, Video, FileImage, Clock, CheckCircle
} from 'lucide-react'

interface Material {
    id: string
    title: string
    subject: string
    chapter: string
    type: 'pdf' | 'video' | 'image' | 'notes'
    size: string
    uploadedAt: string
    uploadedBy: string
    viewed: boolean
}

const subjectColors: Record<string, string> = {
    'Physics': 'bg-blue-500',
    'Chemistry': 'bg-green-500',
    'Biology': 'bg-purple-500',
}

const typeIcons: Record<string, React.ElementType> = {
    'pdf': FileText,
    'video': Video,
    'image': FileImage,
    'notes': BookOpen,
}

export function StudyMaterialView() {
    const [searchQuery, setSearchQuery] = useState('')
    const [selectedSubject, setSelectedSubject] = useState<string>('all')
    const [expandedChapter, setExpandedChapter] = useState<string | null>(null)

    const materials: Material[] = [
        { id: '1', title: 'Thermodynamics Complete Notes', subject: 'Physics', chapter: 'Thermodynamics', type: 'pdf', size: '2.4 MB', uploadedAt: '2 days ago', uploadedBy: 'Dr. Sarah', viewed: true },
        { id: '2', title: 'Laws of Motion - Problem Set', subject: 'Physics', chapter: 'Mechanics', type: 'pdf', size: '1.8 MB', uploadedAt: '3 days ago', uploadedBy: 'Dr. Sarah', viewed: true },
        { id: '3', title: 'Organic Chemistry Reactions Video', subject: 'Chemistry', chapter: 'Organic Chemistry', type: 'video', size: '125 MB', uploadedAt: '1 week ago', uploadedBy: 'Prof. Kumar', viewed: false },
        { id: '4', title: 'Periodic Table Quick Reference', subject: 'Chemistry', chapter: 'Inorganic Chemistry', type: 'image', size: '450 KB', uploadedAt: '1 week ago', uploadedBy: 'Prof. Kumar', viewed: true },
        { id: '5', title: 'Human Physiology Diagrams', subject: 'Biology', chapter: 'Human Physiology', type: 'image', size: '3.2 MB', uploadedAt: '5 days ago', uploadedBy: 'Dr. Meera', viewed: false },
        { id: '6', title: 'Cell Biology Lecture Notes', subject: 'Biology', chapter: 'Cell Biology', type: 'notes', size: '890 KB', uploadedAt: '1 week ago', uploadedBy: 'Dr. Meera', viewed: true },
        { id: '7', title: 'Wave Optics Formulas', subject: 'Physics', chapter: 'Optics', type: 'pdf', size: '560 KB', uploadedAt: '4 days ago', uploadedBy: 'Dr. Sarah', viewed: false },
        { id: '8', title: 'Electrochemistry Important Notes', subject: 'Chemistry', chapter: 'Electrochemistry', type: 'notes', size: '720 KB', uploadedAt: '6 days ago', uploadedBy: 'Prof. Kumar', viewed: false },
    ]

    const subjects = ['all', 'Physics', 'Chemistry', 'Biology']

    const filteredMaterials = materials.filter(m => {
        const matchesSubject = selectedSubject === 'all' || m.subject === selectedSubject
        const matchesSearch = m.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
            m.chapter.toLowerCase().includes(searchQuery.toLowerCase())
        return matchesSubject && matchesSearch
    })

    // Group by chapter
    const groupedByChapter = filteredMaterials.reduce((acc, material) => {
        const key = `${material.subject} - ${material.chapter}`
        if (!acc[key]) acc[key] = []
        acc[key].push(material)
        return acc
    }, {} as Record<string, Material[]>)

    const handleView = (_material: Material) => {
        // Open PDF in new tab - using PROJECT_SCOPE_FF.pdf as example
        // In production, this would use material-specific URLs
        const pdfPath = '/PROJECT_SCOPE_FF.pdf'
        window.open(pdfPath, '_blank')
    }

    return (
        <div className="space-y-4 md:space-y-6 pb-24 lg:pb-0">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <h2 className="text-xl md:text-2xl font-bold text-theme-primary">Study Materials</h2>
                <span className="text-sm text-theme-secondary">{materials.length} materials available</span>
            </div>

            {/* Search & Filter */}
            <div className="flex flex-col sm:flex-row gap-3">
                {/* Search */}
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <input
                        type="text"
                        placeholder="Search materials..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm focus:ring-2 focus:ring-primary-blue/20 focus:border-primary-blue transition-all"
                    />
                </div>

                {/* Subject Filter */}
                <div className="flex bg-slate-100 dark:bg-slate-800 p-1 rounded-xl shrink-0">
                    {subjects.map((subject) => (
                        <button
                            key={subject}
                            onClick={() => setSelectedSubject(subject)}
                            className={`px-3 md:px-4 py-2 text-xs md:text-sm font-medium rounded-lg capitalize transition-all ${selectedSubject === subject
                                ? 'bg-white dark:bg-slate-700 shadow text-primary-blue'
                                : 'text-slate-500 hover:text-slate-700'
                                }`}
                        >
                            {subject === 'all' ? 'All' : subject}
                        </button>
                    ))}
                </div>
            </div>

            {/* Materials List - Grouped by Chapter */}
            <div className="space-y-3">
                {Object.entries(groupedByChapter).map(([chapterKey, chapterMaterials]) => (
                    <GlassmorphicCard key={chapterKey} className="overflow-hidden">
                        {/* Chapter Header */}
                        <button
                            onClick={() => setExpandedChapter(expandedChapter === chapterKey ? null : chapterKey)}
                            className="w-full flex items-center justify-between p-4 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                        >
                            <div className="flex items-center gap-3">
                                <div className={`w-10 h-10 rounded-xl ${subjectColors[chapterMaterials[0].subject] || 'bg-slate-500'} flex items-center justify-center`}>
                                    <FolderOpen className="h-5 w-5 text-white" />
                                </div>
                                <div className="text-left">
                                    <p className="font-semibold text-theme-primary text-sm md:text-base">{chapterKey.split(' - ')[1]}</p>
                                    <p className="text-xs text-theme-secondary">{chapterMaterials[0].subject} • {chapterMaterials.length} items</p>
                                </div>
                            </div>
                            <ChevronDown className={`h-5 w-5 text-slate-400 transition-transform ${expandedChapter === chapterKey ? 'rotate-180' : ''}`} />
                        </button>

                        {/* Chapter Materials */}
                        {expandedChapter === chapterKey && (
                            <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                className="border-t border-slate-100 dark:border-slate-800"
                            >
                                {chapterMaterials.map((material) => {
                                    const TypeIcon = typeIcons[material.type] || FileText

                                    return (
                                        <div
                                            key={material.id}
                                            className="flex items-center justify-between p-4 hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors border-b border-slate-50 dark:border-slate-800/50 last:border-b-0"
                                        >
                                            <div className="flex items-center gap-3 min-w-0 flex-1">
                                                <div className={`w-9 h-9 rounded-lg ${material.type === 'pdf' ? 'bg-red-100 text-red-500' : material.type === 'video' ? 'bg-purple-100 text-purple-500' : 'bg-blue-100 text-blue-500'} flex items-center justify-center shrink-0`}>
                                                    <TypeIcon className="h-4 w-4" />
                                                </div>
                                                <div className="min-w-0 flex-1">
                                                    <div className="flex items-center gap-2">
                                                        <p className="font-medium text-theme-primary text-sm truncate">{material.title}</p>
                                                        {material.viewed && <CheckCircle className="h-3.5 w-3.5 text-green-500 shrink-0" />}
                                                    </div>
                                                    <div className="flex items-center gap-2 text-xs text-theme-secondary">
                                                        <span>{material.size}</span>
                                                        <span>•</span>
                                                        <Clock className="h-3 w-3" />
                                                        <span>{material.uploadedAt}</span>
                                                        <span className="hidden sm:inline">• by {material.uploadedBy}</span>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Actions */}
                                            <div className="flex items-center gap-2 shrink-0 ml-3">
                                                <button
                                                    onClick={() => handleView(material)}
                                                    className="p-2 rounded-lg bg-primary-blue/10 text-primary-blue hover:bg-primary-blue hover:text-white transition-colors"
                                                    title="View"
                                                >
                                                    <Eye className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </div>
                                    )
                                })}
                            </motion.div>
                        )}
                    </GlassmorphicCard>
                ))}

                {Object.keys(groupedByChapter).length === 0 && (
                    <GlassmorphicCard className="p-12 text-center">
                        <BookOpen className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                        <p className="text-theme-secondary">No materials found</p>
                        <p className="text-sm text-slate-400 mt-1">Try adjusting your search or filter</p>
                    </GlassmorphicCard>
                )}
            </div>
        </div>
    )
}
